import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "@/lib/zod-lt";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Combobox } from "@/components/Combobox";
import { DriverForm } from "@/components/DriverForm";
import { TransportForm } from "@/components/TransportForm";
import { useStore } from "@/store";
import { DriverType, TransportType, WaybillType } from "@/types";

const DRIVER_ID_FIELD = "driver_id";
const TRANSPORT_ID_FIELD = "transport_id";
const LOADING_DATE_FIELD = "loading_date";
const LOADING_TIME_FIELD = "loading_time";
const LOADING_LOCATION_FIELD = "loading_location";
const UNLOADING_DATE_FIELD = "unloading_date";
const UNLOADING_TIME_FIELD = "unloading_time";
const UNLOADING_LOCATION_FIELD = "unloading_location";
const CARGO_DESCRIPTION_FIELD = "cargo_description";
const CARGO_WEIGHT_FIELD = "cargo_weight";
const CARGO_QUANTITY_FIELD = "cargo_quantity";

const waybillSchema = z.object({
  [DRIVER_ID_FIELD]: z.number({ message: "Pasirinkite vairuotoją." }),
  [TRANSPORT_ID_FIELD]: z.number({ message: "Pasirinkite transportą." }),
  [LOADING_DATE_FIELD]: z.date({ message: "Pasirinkite pakrovimo datą." }),
  [LOADING_TIME_FIELD]: z.string().optional(),
  [LOADING_LOCATION_FIELD]: z.string().optional(),
  [UNLOADING_DATE_FIELD]: z.date({ message: "Pasirinkite iškrovimo datą." }),
  [UNLOADING_TIME_FIELD]: z.string().optional(),
  [UNLOADING_LOCATION_FIELD]: z.string().optional(),
  [CARGO_DESCRIPTION_FIELD]: z.string().optional(),
  [CARGO_WEIGHT_FIELD]: z.number().optional(),
  [CARGO_QUANTITY_FIELD]: z.number().optional(),
});

const intl = new Intl.DateTimeFormat("lt-LT", { dateStyle: "long" });

type WaybillFormProps = {
  onWaybillFormSubmit?: (waybill: {
    driver_id: number;
    transport_id: number;
    loading_date?: string;
    loading_location?: string;
    unloading_date?: string;
    unloading_location?: string;
    cargo_description?: string;
    cargo_weight?: number;
    cargo_quantity?: number;
  }) => void;
  invoiceId: number;
  waybill?: WaybillType | null;
};

export function WaybillForm({
  onWaybillFormSubmit,
  invoiceId,
  waybill,
}: WaybillFormProps): React.JSX.Element {
  const {
    drivers,
    transports,
    tenant,
    setDrivers,
    setTransports,
    createWaybill,
    updateWaybill,
    isCreateWaybillLoading,
    isUpdateWaybillLoading,
  } = useStore((s) => s);
  const [isDriversComboboxOpen, setDriversComboboxOpen] = useState<boolean>(false);
  const [isTransportsComboboxOpen, setTransportsComboboxOpen] = useState<boolean>(false);
  const [isCreateDriverDialogOpen, setCreateDriverDialogOpen] = useState<boolean>(false);
  const [isCreateTransportDialogOpen, setCreateTransportDialogOpen] = useState<boolean>(false);

  // Parse dates from waybill if editing
  const loadingDate = waybill?.loading_date ? new Date(waybill.loading_date) : new Date();
  const unloadingDate = waybill?.unloading_date ? new Date(waybill.unloading_date) : new Date();
  const loadingTime = waybill?.loading_date
    ? new Date(waybill.loading_date).toTimeString().slice(0, 5)
    : new Date().toTimeString().slice(0, 5);
  const unloadingTime = waybill?.unloading_date
    ? new Date(waybill.unloading_date).toTimeString().slice(0, 5)
    : "";

  const form = useForm<z.infer<typeof waybillSchema>>({
    mode: "all",
    resolver: zodResolver(waybillSchema),
    defaultValues: {
      [DRIVER_ID_FIELD]: waybill?.driver_id ?? tenant?.default_driver_id ?? undefined,
      [TRANSPORT_ID_FIELD]: waybill?.transport_id ?? tenant?.default_transport_id ?? undefined,
      [LOADING_DATE_FIELD]: loadingDate,
      [LOADING_TIME_FIELD]: loadingTime,
      [LOADING_LOCATION_FIELD]: waybill?.loading_location ?? tenant?.address ?? "",
      [UNLOADING_DATE_FIELD]: unloadingDate,
      [UNLOADING_TIME_FIELD]: unloadingTime,
      [UNLOADING_LOCATION_FIELD]: waybill?.unloading_location ?? "",
      [CARGO_DESCRIPTION_FIELD]: waybill?.cargo_description ?? "",
      [CARGO_WEIGHT_FIELD]: waybill?.cargo_weight ?? undefined,
      [CARGO_QUANTITY_FIELD]: waybill?.cargo_quantity ?? undefined,
    },
  });

  const selectedDriver = drivers?.find((driver) => driver.id === form.getValues(DRIVER_ID_FIELD));
  const selectedDriverOption = selectedDriver
    ? { name: `${selectedDriver.name} (${selectedDriver.license_number})`, id: selectedDriver.id }
    : null;

  const selectedTransport = transports?.find(
    (transport) => transport.id === form.getValues(TRANSPORT_ID_FIELD),
  );
  const selectedTransportOption = selectedTransport
    ? {
        name: `${selectedTransport.license_plate} - ${selectedTransport.model}`,
        id: selectedTransport.id,
      }
    : null;

  const driverOptions = drivers
    .map(({ name, license_number, id }) => ({
      name: `${name} (${license_number})`,
      id,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const transportOptions = transports
    .map(({ license_plate, model, id }) => ({
      name: `${license_plate} - ${model}`,
      id,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  function handleDriverSelect(option: { id: string | number }): void {
    const id = typeof option.id === "number" ? option.id : Number(option.id);
    if (!isNaN(id)) {
      form.setValue(DRIVER_ID_FIELD, id);
      form.trigger(DRIVER_ID_FIELD);
    }
  }

  function handleTransportSelect(option: { id: string | number }): void {
    const id = typeof option.id === "number" ? option.id : Number(option.id);
    if (!isNaN(id)) {
      form.setValue(TRANSPORT_ID_FIELD, id);
      form.trigger(TRANSPORT_ID_FIELD);
    }
  }

  function handleDriverCreate(driverFormData: {
    name: string;
    license_number: string;
    id?: number;
  }) {
    if (!driverFormData.id) return;
    const newDriver =
      drivers.find((d) => d.id === driverFormData.id) ??
      ({
        id: driverFormData.id,
        name: driverFormData.name,
        license_number: driverFormData.license_number,
      } as DriverType);
    setDrivers([newDriver, ...drivers]);
    setCreateDriverDialogOpen(false);
    setDriversComboboxOpen(false);
    form.setValue(DRIVER_ID_FIELD, driverFormData.id);
    form.trigger(DRIVER_ID_FIELD);
  }

  function handleTransportCreate(transportFormData: {
    license_plate: string;
    model: string;
    id?: number;
  }) {
    if (!transportFormData.id) return;
    const newTransport =
      transports.find((t) => t.id === transportFormData.id) ??
      ({
        id: transportFormData.id,
        license_plate: transportFormData.license_plate,
        model: transportFormData.model,
      } as TransportType);
    setTransports([newTransport, ...transports]);
    setCreateTransportDialogOpen(false);
    setTransportsComboboxOpen(false);
    form.setValue(TRANSPORT_ID_FIELD, transportFormData.id);
    form.trigger(TRANSPORT_ID_FIELD);
  }

  function combineDateAndTime(date: Date, time: string): Date {
    if (!time) return date;
    const [hours, minutes] = time.split(":").map(Number);
    const combined = new Date(date);
    combined.setHours(hours || 0, minutes || 0, 0, 0);
    return combined;
  }

  async function onSubmit(formData: z.infer<typeof waybillSchema>): Promise<void> {
    const loadingDateTime = combineDateAndTime(
      formData[LOADING_DATE_FIELD],
      formData[LOADING_TIME_FIELD] ?? "",
    );
    const unloadingDateTime = combineDateAndTime(
      formData[UNLOADING_DATE_FIELD],
      formData[UNLOADING_TIME_FIELD] ?? "",
    );

    const waybillData = {
      invoice_id: invoiceId,
      driver_id: formData[DRIVER_ID_FIELD],
      transport_id: formData[TRANSPORT_ID_FIELD],
      loading_date: loadingDateTime.toISOString(),
      loading_location: formData[LOADING_LOCATION_FIELD] || undefined,
      unloading_date: unloadingDateTime.toISOString(),
      unloading_location: formData[UNLOADING_LOCATION_FIELD] || undefined,
      cargo_description: formData[CARGO_DESCRIPTION_FIELD] || undefined,
      cargo_weight: formData[CARGO_WEIGHT_FIELD] || undefined,
      cargo_quantity: formData[CARGO_QUANTITY_FIELD] || undefined,
    };

    if (waybill?.id) {
      await updateWaybill(waybill.id, waybillData);
    } else {
      await createWaybill(waybillData);
    }

    onWaybillFormSubmit?.(waybillData);
  }

  const format = (date: Date) => intl.format(new Date(date));

  const buttonIdleLabel = waybill?.id ? "Atnaujinti" : "Sukurti";
  const buttonLoadingLabel = waybill?.id ? "Atnaujinama..." : "Kuriama...";
  const isLoading = isCreateWaybillLoading || isUpdateWaybillLoading;
  const buttonLabel = isLoading ? buttonLoadingLabel : buttonIdleLabel;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <FormField
            control={form.control}
            name={DRIVER_ID_FIELD}
            render={() => (
              <FormItem className="flex flex-col">
                <FormLabel>Vairuotojas*</FormLabel>
                <Combobox
                  options={driverOptions}
                  isOpen={isDriversComboboxOpen}
                  onComboboxOpenChange={setDriversComboboxOpen}
                  selectedOption={selectedDriverOption}
                  onSelect={handleDriverSelect}
                  selectLabel="Pasirinkite vairuotoją"
                  searchLabel="Ieškoti vairuotojo..."
                  notFoundLabel="Vairuotojas nerastas."
                  dialogTitle="Naujas vairuotojas"
                  dialogDescription="Sukurkite naują vairuotoją."
                  isDialogOpen={isCreateDriverDialogOpen}
                  onDialogOpenChange={setCreateDriverDialogOpen}
                >
                  <DriverForm onDriverFormSubmit={handleDriverCreate} />
                </Combobox>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={TRANSPORT_ID_FIELD}
            render={() => (
              <FormItem className="flex flex-col">
                <FormLabel>Transportas*</FormLabel>
                <Combobox
                  options={transportOptions}
                  isOpen={isTransportsComboboxOpen}
                  onComboboxOpenChange={setTransportsComboboxOpen}
                  selectedOption={selectedTransportOption}
                  onSelect={handleTransportSelect}
                  selectLabel="Pasirinkite transportą"
                  searchLabel="Ieškoti transporto..."
                  notFoundLabel="Transportas nerastas."
                  dialogTitle="Naujas transportas"
                  dialogDescription="Sukurkite naują transportą."
                  isDialogOpen={isCreateTransportDialogOpen}
                  onDialogOpenChange={setCreateTransportDialogOpen}
                >
                  <TransportForm onTransportFormSubmit={handleTransportCreate} />
                </Combobox>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={LOADING_DATE_FIELD}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Pakrovimo data*</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        {field.value ? format(field.value) : <span>Pasirinkite datą</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={LOADING_TIME_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pakrovimo laikas</FormLabel>
                <FormControl>
                  <Input type="time" {...field} />
                </FormControl>
                <FormDescription>Laikas formatu HH:MM</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={LOADING_LOCATION_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pakrovimo vieta</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={UNLOADING_DATE_FIELD}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Iškrovimo data*</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        {field.value ? format(field.value) : <span>Pasirinkite datą</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={UNLOADING_TIME_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Iškrovimo laikas</FormLabel>
                <FormControl>
                  <Input type="time" {...field} />
                </FormControl>
                <FormDescription>Laikas formatu HH:MM</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={UNLOADING_LOCATION_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Iškrovimo vieta</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={CARGO_DESCRIPTION_FIELD}
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Krovinio aprašymas</FormLabel>
                <FormControl>
                  <Textarea {...field} rows={3} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={CARGO_WEIGHT_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Krovinio svoris</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === "" ? undefined : Number(value));
                    }}
                  />
                </FormControl>
                <FormDescription>Kilogramais</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={CARGO_QUANTITY_FIELD}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Krovinio kiekis</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === "" ? undefined : Number(value));
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
          {buttonLabel}
        </Button>
      </form>
    </Form>
  );
}
